// `isMainnetEnabled()` — single source of truth for whether mainnet
// trading is open for business.
//
// Backed by the `mainnet_enabled` Statsig gate (surface: `environment`).
// Default: `false`. Flip the gate ON in Statsig the day mainnet opens.
//
// Drives:
//   - `NetworkLink` — when off, every demo-mode-aware href gets
//     `/demo-mode/` prepended.
//   - `<MainnetGateMirror>` — mirrors this value to a cookie middleware
//     reads to short-circuit redirects.
//   - `resolveAuthRedirect()` callers — when off, post-auth/onboarding
//     navigations land at `/demo-mode/<intended-path>` instead of bare.
//
// Resolution order:
//   1. DevToolkit override — read DIRECTLY from the Zustand store
//      (bypassing `getDevToolkitOverride`'s `accessGranted` gate). The
//      access gate is a UI-shaping guard for the analytics provider's
//      ledger, not a security boundary; for THIS particular gate, an
//      engineer flipping the override expects it to take effect on
//      first paint without waiting for the DevToolkit panel to mount.
//   2. Statsig live value via `checkGate` — picks up the production gate
//      state (and the noop fallback to the registry default) for non-
//      override callers.
//
// Distinct from `isNetworkConfigured('mainnet')`: that checks "are the
// mainnet env vars populated"; this checks "is the gate ON". Mainnet may
// be technically configured (env vars present) but still gated off until
// launch day.

import { checkGate } from '@/lib/analytics';
import { useDevToolkitStore } from '@/stores/dev-toolkit';

export function isMainnetEnabled(): boolean {
  // Direct override read — Zustand's `getState` is synchronous and
  // works in any context (SSR, render, effect, plain function).
  const overrides = useDevToolkitStore.getState().overrides;
  if (Object.hasOwn(overrides, 'mainnet_enabled')) {
    const override = overrides.mainnet_enabled;
    if (typeof override === 'boolean') return override;
  }
  return checkGate('mainnet_enabled');
}
